import { Availability } from '@c/dashboard/pages/ListingsPage';
import { CategoryDocument } from '@models/category';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import { logEvent } from '@util/analytics';
import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import { BaseGetProductsArgs } from '@util/getPropsFromContext';
import { client } from '@util/getTypesense';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import {
  getSearchProductsUrl,
  isMobile,
  nonNullable,
  normalize,
} from '@util/index';
import { logError } from '@util/logError';
import { ReasonTypes } from '@util/maps/report-reasons';
import { BlogDocument } from '@util/types/firestore/blog';
import { DataDepot } from 'app/data-depot/_components/DataDepotForm';
import { SalePageResponse } from 'app/sale/[curated-list]/page';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  CollectionReference,
  deleteField,
  doc,
  DocumentData,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocsFromCache,
  increment,
  limit,
  loadBundle,
  namedQuery,
  orderBy,
  Query,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { BannerDocument } from 'models/banner';
import { CuratedListDocument } from 'models/curated_list';
import {
  CauseDocument,
  Condition,
  FacetCounts,
  GetForYouProductsOptions,
  GetProductArgs,
  ProductDocument,
  ProductLight,
  ProductResultsPaginated,
  ProductResultsPaginatedByRef,
  productSchema,
  ProductSortOption,
  Variation,
} from 'models/product';
import { ApiResponse, Bike, RideType } from 'models/shared';
import { TrendingSearch } from 'models/trending-search';
import {
  PublicUserDocument,
  Sports,
  UserDocument,
  UserProfile,
} from 'models/user';
import { SearchParamsWithPreset } from 'typesense/lib/Typesense/Documents';
import { MultiSearchRequestSchema } from 'typesense/lib/Typesense/MultiSearch';
import { getBlogs } from '../blog';
import { getBrandByName } from '../brands/brands.service';
import { getCategoryByID } from '../categories';
import { isBot } from 'next/dist/server/web/spec-extension/user-agent';
import { stripHtml } from 'string-strip-html';
export const DEFAULT_LIMIT: number = 12;
export const EUROPE_CODES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

export const SOUTH_AMERICA_CODES = [
  'AR',
  'BO',
  'BR',
  'CL',
  'CO',
  'EC',
  'FK',
  'GF',
  'GY',
  'PY',
  'PE',
  'SR',
  'UY',
  'VE',
];

export const productsRef = collection(
  db,
  'products'
) as CollectionReference<ProductDocument>;

export const usersRef = collection(
  db,
  'users'
) as CollectionReference<UserDocument>;

type HomePageData = {
  'top-chips-query': {
    index: number;
    label: string;
    route: string;
    icon?: string;
  }[];
  'banners-query': BannerDocument[];
  'auctions-query': ProductLight[];
  'flash-deals-query': ProductLight[];
  'trending-products-query': ProductLight[];
  'recent-bikes-query': ProductLight[];
  'featured-blogs-query': BlogDocument[];
  'featured-sellers-query': PublicUserDocument[];
  'seller1-products-query': ProductLight[];
  'seller2-products-query': ProductLight[];
  'seller3-products-query': ProductLight[];
  'sold-products-query': ProductLight[];
  'onsale-products-query': ProductLight[];
  'curated-lists-query': CuratedListDocument[];
  'trending-searches-query': TrendingSearch[];
  'trending-riding-gear-query': ProductLight[];
  'trending-bike-parts-query': ProductLight[];
};

const fetchBundle = async () => {
  try {
    const url = process.env.NEXT_PUBLIC_IS_STAGING
      ? 'https://storage.googleapis.com/mx-locker-staging.appspot.com/common-files/firestore-bundle.txt'
      : 'https://bundle.mxlocker.com/firestore-bundle.txt';
    const res = await fetch(url);
    const text = await res.text();
    await loadBundle(db, text);
  } catch (e) {
    logError('homeBundle: ' + (e as Error).message);
  }
};

export const getHomePageData = async () => {
  const url = process.env.NEXT_PUBLIC_IS_STAGING
    ? 'https://storage.googleapis.com/mx-locker-staging.appspot.com/common-files/firestore-bundle.json'
    : 'https://bundle.mxlocker.com/firestore-bundle.json';
  const res = await fetch(url, {
    headers: { 'content-type': 'application/json' },
    next: { revalidate: 1800 }, // matches homepage revalidate
  });
  const jsonData: HomePageData = await res.json();
  return jsonData;
};

export const getSalePageData = async (slug: string) => {
  const url = `https://us-central1-${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net/v2_getSalePage`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        slug,
      },
    }),
  });
  const data = (await res.json()) as { result: SalePageResponse };
  return data.result;
};

export const getProductsByIdQuery = (ids: string[]) =>
  query(productsRef, where('id', 'in', [...ids]));

export const getAuctionsQuery = () =>
  query(
    productsRef,
    where('is_auction', '==', true),
    where('out_of_stock', '==', false),
    // order by ending soonest
    orderBy('end_time', 'asc'),
    limit(10)
  );

export async function getProductBySlug(
  args: GetProductArgs
): Promise<ApiResponse<ProductDocument | null>> {
  if (args.slug && args.slug.length === 16 && !isNaN(args.slug as any)) {
    const product = await getProductByItemNumber(+args.slug);
    if (product) return { results: product };
  }
  const q = query(productsRef, where('slug', '==', args.slug), limit(1));
  const snapshot = await getDocs(q);
  const data = snapshot.docs?.[0]?.data() ?? null;
  if (!data) {
    const product = await getProductById(args.slug ?? '');
    if (product) return { results: product };
    return { results: null };
  }
  return { results: cleanLocation(data) };
}

export async function getProductByItemNumber(
  num: number
): Promise<ProductDocument | null> {
  const q = query(productsRef, where('item_number', '==', num), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.docs?.[0]?.data() ?? null;
}

export async function getProductByIdNotClean(id: string) {
  const d = doc(productsRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? snapshot.data() : null;
}

export async function getProductById(id: string) {
  const d = doc(productsRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? cleanLocation(snapshot.data()) : null;
}

export async function getProductBySku(sku: string): Promise<DataDepot | null> {
  const normalizedSku = normalize(sku);
  if (!normalizedSku) return null;
  const dataDepotRef = collection(db, 'data_depot');
  const res = await getDocs(
    query(
      dataDepotRef,
      where('normalized_skus', 'array-contains', normalizedSku)
    )
  );
  const results = res.docs.map((doc) => doc.data());

  if (results.length) {
    return results[0] as DataDepot;
  }

  return null;
}

export async function getProductByMpn(mpn: string[]) {
  const dataDepotRef = collection(db, 'data_depot');
  const docs = [];
  const chunks = mpn.reduce((acc, curr, index) => {
    const chunkIndex = Math.floor(index / 30);
    if (!acc[chunkIndex]) {
      acc[chunkIndex] = [];
    }
    acc[chunkIndex].push(curr);
    return acc;
  }, [] as string[][]);
  for (const chunk of chunks) {
    const res = await getDocs(
      query(
        dataDepotRef,
        where('normalized_skus', 'array-contains-any', chunk.map(normalize))
      )
    );
    docs.push(...res.docs.map((doc) => doc.data()));
  }
  return (docs[0] ?? null) as ProductDocument | null;
}

export async function getProductFromDataDepotById(id: string) {
  const dataDepotRef = collection(db, 'data_depot');
  const d = doc(dataDepotRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? (snapshot.data() as ProductDocument) : null;
}

export async function getMostRecentProductSlugs(): Promise<
  ApiResponse<string[]>
> {
  const q = query(productsRef, orderBy('date_added', 'desc'), limit(1000));
  const docsResponse = await getDocs(q);
  const slugs = docsResponse.docs.map((doc) => doc.get('slug')).filter(Boolean);
  return {
    results: slugs,
  };
}

export async function getProductsByCategory(categories: string[]) {
  const q1 = query(
    productsRef,
    where('categories', 'array-contains', categories[0]),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(categories.length === 1 ? 20 : 10)
  );

  if (categories.length === 1) {
    const snapshot1 = await getDocs(q1);
    return [[...snapshot1.docs.map((doc) => cleanLocation(doc.data()))]];
  }

  const q2 = query(
    productsRef,
    where('categories', 'array-contains', categories[1]),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(10)
  );
  const snapshot1 = await getDocs(q1);
  const snapshot2 = await getDocs(q2);

  const products = [
    [...snapshot1.docs.map((doc) => cleanLocation(doc.data()))],
    [...snapshot2.docs.map((doc) => cleanLocation(doc.data()))],
  ];
  return products;
}

export async function getProductsByIds(
  ids: string[],
  dontClean?: boolean
): Promise<ProductDocument[]> {
  if (ids.length === 0) return [];

  const chunkSize = 30;
  const chunks = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }

  const products: ProductDocument[] = [];
  for (const chunk of chunks) {
    const q = query(productsRef, where('id', 'in', chunk));
    const snapshot = await getDocs(q);
    products.push(
      ...snapshot.docs.map((d) =>
        dontClean ? d.data() : cleanLocation(d.data())
      )
    );
  }

  return products;
}

export async function getProductsBySellerId(
  sellerId: string,
  l = DEFAULT_LIMIT,
  currProductId?: string
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('seller_id', '==', sellerId),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l),
    ...(currProductId ? [where('id', '!=', currProductId)] : [])
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export async function getLiveListingsCountFromTypesense(
  sellerId: string
): Promise<number> {
  if (typeof window !== 'undefined' && isBot(window.navigator.userAgent)) {
    return 0;
  }
  const products = await client
    .collections('products_alias')
    .documents()
    .search({
      q: '',
      query_by: 'title',
      filter_by: `seller_id:${sellerId}`,
    });
  return products.found;
}

export async function getDraftProductsBySellerId(seller_id: string) {
  const q = query(
    productsRef,
    where('seller_id', '==', seller_id),
    where('is_draft', '==', true)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export async function getProductsByPurchaseEntryID(
  purchaseEntryID: string
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('purchase_entry_id', '==', purchaseEntryID),
    orderBy('date_added', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export interface GetDirtBikesArgs {
  page: number;
  onSale?: boolean;
  types: string[];
  bikeMakes: string[];
  bikeModels: string[];
  bikeYears: string[];
  engineSizes: string[];
  conditions: Condition[];
  minPrice: number | null;
  maxPrice: number | null;
  sort: ProductSortOption;
  location?: {
    lat: number;
    lng: number;
    address: string;
  } | null;
  radius?: number;
  keyword: string;
  brands: string[];
  is_featured?: boolean;
  letterSizes: string[];
  numberSizes: string[];
}

export const getStaticMapUrl = (lat: number, lng: number) => {
  // Don't want to give away exact location :)
  lat = +lat.toFixed(2);
  lng = +lng.toFixed(2);
  const size = isMobile() ? '300x300' : '500x500';
  var uri = 'https://maps.googleapis.com/maps/api/staticmap?';
  var staticMapSrc = 'center=' + lat + ',' + lng;
  staticMapSrc += '&zoom=6';
  staticMapSrc += `&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`;
  staticMapSrc += '&size=' + size;
  return uri + encodeURI(staticMapSrc);
};

type SearchRequestBody = {
  params: Pick<
    SearchParamsWithPreset,
    | 'q'
    | 'page'
    | 'per_page'
    | 'filter_by'
    | 'facet_by'
    | 'max_facet_values'
    | 'sort_by'
    | 'preset'
  >;
  user?: Pick<UserDocument, 'sports' | 'sizes' | 'gender'>;
  uid?: string;
};

export function getTypesenseSortBy(sort: ProductSortOption) {
  if (sort === 'priceAsc') return 'price:asc';
  if (sort === 'priceDesc') return 'price:desc';
  if (sort === 'newest') return `date_added:desc`;
  if (sort === 'bestMatch') return '';
  if (sort === 'soldDate') return 'sold_date:desc';
  return 'date_added:desc';
}

function sportToRideType(sport: keyof Sports): RideType {
  switch (sport) {
    case 'mx':
      return 'Dirt Bikes';
    case 'snow':
      return 'Snow';
    case 'atv':
      return 'ATV';
    case 'utv':
      return 'UTV';
    case 'street':
      return 'Street';
    case 'adv':
      return 'Adventure';
    case 'cycling':
      return 'Cycling';
    case 'mtb':
      return 'Mountain Bikes';
    default:
      return 'Dirt Bikes';
  }
}

function getRideTypesFromSports(sports: Sports) {
  const ride_types: RideType[] = ['Dirt Bikes'];
  if (sports)
    for (const sport in sports) {
      const key = sport as keyof Sports;
      if (sports[key]) {
        const rt = sportToRideType(key);
        if (rt !== 'Dirt Bikes') ride_types.push(rt);
      }
    }
  return ride_types;
}

// TODO
export async function getTypesenseEvalBy(uid?: string, keyword?: string) {
  if (!uid) return '';
  const host =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:8100'
      : process.env.NEXT_PUBLIC_IS_STAGING
      ? 'https://mxlocker.dev'
      : 'https://mxlocker.com';
  const res = await fetch(`${host}/api/get-user`, {
    method: 'POST',
    body: JSON.stringify({ uid }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401) {
    return '';
  }
  const { user } = (await res.json()) as { user: UserDocument };
  if (!user) return '';
  const rt = getRideTypesFromSports(user.sports).join(',');
  const lower = keyword?.toLowerCase().trim();
  if (!lower) return '';
  if (lower === 'a kit' || lower == 'a-kit' || lower === 'cone valve') {
    return `_eval([(ride_types:=[${rt}]):3,(category1:=Suspension):2,(brand:=[WP,KYB,\`Pro Circuit\`,Ohlins,Showa]):1]):desc`;
  } else if (lower === 'fasthouse' || lower === 'fast house') {
    if (user?.sizes?.jersey && user.sizes.pants && user.sizes.helmet)
      return `_eval([(category2:=\`Gear Combos\` && size.letter:=${user.sizes.jersey} && size.number:=${user.sizes.pants}):5,
  (category1:=Helmets && size.letter:=${user.sizes.helmet}):4,
  (category2:=Jerseys && size.letter:=${user.sizes.jersey}):3,
  (category2:=Pants && size.number:=${user.sizes.pants}):2,
  (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'chest protector' || lower === 'chest protectors') {
    if (user?.sizes?.jersey)
      return `_eval([(size.letter:=${user.sizes.jersey}):3,(ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'knee braces') {
    if (user?.sizes?.jersey)
      return `_eval([(size.letter:=${user.sizes.jersey}):3,(ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'boots' || lower === 'boot' || /tech\s\d+/.test(lower)) {
    if (user?.sizes?.boots)
      return `_eval([(size.number:=${user.sizes.boots}):3,(ride_types:=[${rt}]):2,
  (gender:=[${user.gender},U]):1]):desc`;
  } else if (
    lower === 'alpinestar' ||
    lower === 'alpinestars' ||
    lower === 'fox'
  ) {
    if (user)
      return `_eval([(size.number:=${user.sizes.boots}):2,
  (category2:=\`Gear Combos\` && size.letter:=${user.sizes.jersey} && size.number:=${user.sizes.pants}):2,
  (category1:=Helmets && size.letter:=${user.sizes.helmet}):2,
  (category2:=Jerseys && size.letter:=${user.sizes.jersey}):2,
  (category2:=Pants && size.number:=${user.sizes.pants}):2,
  (gender:=[${user.gender},U]):1]):desc`;
  } else if (
    lower === 'helmet' ||
    lower === 'helmets' ||
    lower === 'bell moto 10' ||
    lower === 'sm10' ||
    lower === 'sm 10' ||
    lower === 's-m10'
  ) {
    if (user?.sizes?.helmet)
      return `_eval([(size.letter:=${user.sizes.helmet}):3,(ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'gear') {
    if (user?.sizes?.jersey && user?.sizes?.pants)
      return `_eval([(category2:=\`Gear Combos\` && size.letter:=${user.sizes.jersey} && size.number:=${user.sizes.pants}):5,
    (category2:=Jerseys && size.letter:=${user.sizes.jersey}):4, (category2:=Pants && size.number:=${user.sizes.pants}):3,
    (ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'jersey' || lower === 'jerseys') {
    if (user?.sizes?.jersey)
      return `_eval([(category2:=Jerseys && size.letter:=${user.sizes.jersey}):3,
    (ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower === 'pants' || lower === 'pant') {
    if (user?.sizes?.pants)
      return `_eval([(category2:=Pants && size.number:=${user.sizes.pants}):3,
    (ride_types:=[${rt}]):2, (gender:=[${user.gender},U]):1]):desc`;
  } else if (lower.endsWith('engine')) {
    return '_eval(category2:=`Complete Engines`):desc';
  } else if (lower.endsWith('forks')) {
    return '_eval(category2:=Forks):desc';
  }
  return '';
}

// BEGIN: for-you product fetchers

/**
 * Replicates the functionality of getProducts very closely. Duplicated b/c getProducts is
 * used only in the context of getServerSideProps, where its args are provided from the same
 * context every time (server-side). Might be nice to generalize in the future.
 */
export async function getForYouProducts({
  brand,
  category,
  subCategory,
  userBike,
  userSizes,
  searchLimit = DEFAULT_LIMIT,
  sort = 'mostPopular',
  lastDocSnapshot,
}: GetForYouProductsOptions): ProductResultsPaginatedByRef {
  // Set defaults

  const resultLimit = searchLimit ?? DEFAULT_LIMIT;
  const brands = brand ? [brand] : [];

  // Build Firestore query constraints

  let baseConstraints = [
    where('out_of_stock', '==', false),
    getSortConstraint(sort),
    getBrandsConstraint(brands),
  ];

  if (!!category) {
    baseConstraints.push(getCategoryConstraint(category));
  }

  if (!!subCategory) {
    baseConstraints.push(getSubCategoryConstraint(subCategory, null, null));
  }

  if (!!userSizes && !!category && !!subCategory) {
    if (!!userSizes.boots && subCategory === 'Boots') {
      baseConstraints.push(getBootSizesConstraint([`${userSizes?.boots}`]));
    }
    if (!!userSizes.helmet && subCategory === 'Helmets') {
      baseConstraints.push(getHelmetSizesConstraint([userSizes?.helmet]));
    }
    if (!!userSizes.jersey && subCategory === 'Jerseys') {
      baseConstraints.push(getShirtSizesConstraint([userSizes?.jersey]));
    }
    if (!!userSizes.pants && subCategory === 'Pants') {
      baseConstraints.push(getPantSizesConstraint([`${userSizes?.pants}`]));
    }
    if (!!userSizes.gloves && subCategory === 'Gloves') {
      baseConstraints.push(getGloveSizesConstraint([userSizes?.gloves]));
    }
  }

  if (!!userBike) {
    let bikeConstraints = {
      makes: [] as string[],
      models: [] as string[],
      years: [] as string[],
    };
    if (!!userBike.make) {
      bikeConstraints.makes = [userBike.make];
    }
    if (!!userBike.model) {
      bikeConstraints.models = [userBike.model];
    }
    if (!!userBike.year) {
      bikeConstraints.years = [`${userBike.year}`];
    }
    baseConstraints.push(getBikeConstraints(bikeConstraints));
  }

  // Build Firestore query and fetch data

  const nonNullConstraints = baseConstraints.filter(nonNullable);

  const queryBase = query(
    productsRef,
    ...nonNullConstraints,
    limit(resultLimit)
  );

  const qFinal = lastDocSnapshot
    ? query(queryBase, startAfter(lastDocSnapshot))
    : queryBase;

  const docsSnapshot = await getDocs(qFinal);

  const docsResponse = docsSnapshot.docs.map((x) => cleanLocation(x.data()));

  // For pagination
  const lastVisible = docsSnapshot.docs[docsSnapshot.docs.length - 1];

  return {
    results: docsResponse,
    brands: getBrandsFromResult(docsResponse),
    lastDocSnapshot: lastVisible,
  };
}

/**
 * Replicated the functionality of getProducts very closely but excluding filters.
 * Should only be used to fetch personalized+unfiltered results from Typesense.
 * See getForYouProducts for information on why it was decided to replicate this.
 */
export async function getForYouProductsFromTypesense(
  searchLimit: number = DEFAULT_LIMIT,
  page: number = 1,
  category?: string
) {
  const index = 'products_alias';

  let filters = '';
  if (category) {
    filters += `category:"${category}" && `;
  } else {
    filters += `category:!="Dirt Bikes" && `;
  }
  if (filters.endsWith('&& ')) filters = filters.slice(0, -4);

  const res = await client.collections(index).documents().search({
    q: '',
    query_by: 'title',
    per_page: searchLimit,
    page: page,
    filter_by: filters,
  });

  return {
    results: res.hits?.map((h) => cleanLocation(h.document as ProductDocument)),
    brands: getBrandsFromResult(
      res.hits?.map((h) => h.document as ProductDocument) ?? []
    ),
    pageInfo: {
      page: page > res.page ? res.page : page,
      totalPages: res.hits?.length ? Math.ceil(res.found / res.hits.length) : 0,
      itemsPerPage: res.hits?.length ?? 0,
      totalResults: res.found,
    },
  };
}

// END: for-you product fetchers

const mainCats: string[] = ['Riding Gear', 'Bike Parts', 'Collectibles'];

export async function getNewArrivals(
  l = DEFAULT_LIMIT
): Promise<ApiResponse<ProductDocument[]>> {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getTrendingProducts(l = DEFAULT_LIMIT) {
  //  call to typesense
  const res = await client.collections('products_alias').documents().search({
    q: '*',
    query_by: 'title',
    per_page: l,
    sort_by: 'popularity:desc',
    page: 1,
  });
  return {
    results:
      res.hits?.map((h) => cleanLocation(h.document as ProductDocument)) ?? [],
  };
}

export async function getPageNewArrivals(
  l = DEFAULT_LIMIT,
  page: string,
  categoryFilter?: string
): Promise<{
  results: ProductDocument[];
}> {
  const categoryMap: { [key: string]: string } = {
    '/shop/riding-gear': 'Riding Gear',
    '/shop/parts': 'Bike Parts',
    '/shop/accessories': 'Accessories',
  };

  const category = categoryMap[page];
  const conditions = [
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l),
  ];

  if (categoryFilter) {
    conditions.push(where('categories', 'array-contains', categoryFilter));
  } else {
    conditions.push(where('category', 'in', mainCats));
  }

  if (category) {
    conditions.push(where('category', '==', category));
  }

  const q = query(productsRef, ...conditions);
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data() as ProductDocument));
  return { results };
}

export async function getFeaturedProducts(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'featured-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('is_featured', '==', true),
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getFeaturedBikes(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'featured-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('is_featured', '==', true),
    where('category', '==', 'Dirt Bikes'),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRecentBikes(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('category', '==', 'Dirt Bikes'),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getAllAuctionsBySellerId(seller_id: string) {
  const q = query(
    productsRef,
    where('seller_id', '==', seller_id),
    where('is_auction', '==', true),
    // where('auction_name', '==', 'Brian Barnes Ironman'),
    where('is_draft', '==', false),
    where('end_time', '>=', Date.now())
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getLiveAuctions() {
  const q = query(
    productsRef,
    where('is_auction', '==', true),
    where('out_of_stock', '==', false),
    where('is_auction_live', '==', true),
    where('end_time', '>=', Date.now()),
    orderBy('end_time', 'asc'),
    limit(30)
  );

  return await getDocs(q).then((res) => res.docs.map((d) => d.data()));
}

export async function getOnSale(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'onsale-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('on_sale', '==', true),
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getRecentlySold(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'sold-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(productsRef, orderBy('sold_date', 'desc'), limit(l));
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRecentMTB() {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-mountain-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    }
  }
  const q = query(
    productsRef,
    where('category', '==', 'Mountain Bikes'),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(12)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRecentPaddockPosts(): Promise<BlogDocument[]> {
  const { results, pageInfo } = await getBlogs({
    limit: 3,
    pageInfo: {
      cursor: null,
      direction: 'next',
    },
  });

  return results;
}

export async function getRelatedProducts(
  product: ProductDocument,
  l = DEFAULT_LIMIT
) {
  if (typeof window !== 'undefined' && isBot(window.navigator.userAgent)) {
    return {
      results: [],
    };
  }
  const index = 'products_alias';
  const productInTypesense = await client
    .collections<ProductDocument & { embedding: number[] }>(index)
    .documents()
    .search({
      q: product.title,
      query_by: 'title',
      per_page: 1,
    });
  if (!productInTypesense?.hits?.[0]?.document) return { results: [] };
  const productEmbedding = productInTypesense?.hits?.[0]?.document.embedding;
  const vector_query = productEmbedding
    ? `embedding:([${productEmbedding}], k:${l})`
    : null;

  let catIndex = '';
  let catValue = product.category;
  let backupCatValue = product.category;
  let backupCatIndex = '';
  if (product.category3) {
    catIndex = '3';
    catValue = product.category3;
    backupCatValue = product.category2!;
    backupCatIndex = '2';
  } else if (product.category2) {
    catIndex = '2';
    catValue = product.category2;
    backupCatValue = product.category1!;
    backupCatIndex = '1';
  } else if (product.category1) {
    catIndex = '1';
    catValue = product.category1;
    backupCatIndex = '';
  }

  const cat_filter = `category${catIndex}:="${catValue}"`;
  const backup_cat_filter = `category${backupCatIndex}:="${backupCatValue}"`;
  let size_filter = '';
  let bike_filter = '';
  if (product.size?.letter)
    size_filter += ` && (size.letter:=${product.size?.letter} || variations.size:=${product.size?.letter})`;
  if (product.size?.number)
    size_filter += ` && (size.number:=${product.size?.number} || variations.size:=${product.size?.number})`;
  if (
    product.category === 'Bike Parts' &&
    product.fitting_bikes?.bikes?.length
  ) {
    const uniqueMakes = new Set(product.fitting_bikes.bikes.map((b) => b.make));
    const uniqueModels = new Set(
      product.fitting_bikes.bikes.map((b) => b.model)
    );
    // filter_by += ` && (fitting_bikes.bikes.make:=[${uniqueMakes.toString()}] && fitting_bikes.bikes.model:=[${uniqueModels.toString()}])`;
    if (uniqueModels.size) {
      const str = [...uniqueModels.values()].map((m) => `\`${m}\``).join(',');
      bike_filter += ` && (fitting_bikes_strings:[${str}])`;
    } else if (uniqueMakes.size) {
      const str = [...uniqueMakes.values()].map((m) => `\`${m}\``).join(',');
      bike_filter += ` && (fitting_bikes_strings:[${str}])`;
    }
  }

  const searchRequests = {
    searches: [
      {
        collection: index,
        q: '*',
        filter_by: `${cat_filter} ${size_filter} ${bike_filter}`,
        // ...(vector_query ? { vector_query } : {}),
        per_page: l,
        sort_by: 'popularity:desc',
        // sort_by: `_eval(seller_id:=${product.seller_id}):desc`,
      },
      {
        collection: index,
        q: '*',
        filter_by: `${backup_cat_filter} ${size_filter} ${bike_filter}`,
        // ...(vector_query ? { vector_query } : {}),
        per_page: l,
        sort_by: 'popularity:desc',
        // sort_by: `_eval(seller_id:=${product.seller_id}):desc`,
      },
    ] as MultiSearchRequestSchema[],
  };
  const { results } = await client.multiSearch.perform<ProductDocument[]>(
    searchRequests
  );
  const products = (
    results[0]?.hits?.map((h) => h.document as ProductDocument) ?? []
  ).filter((p) => p.id !== product.id);
  if (products.length < l) {
    const moreProducts = (
      results[1]?.hits?.map((h) => h.document as ProductDocument) ?? []
    ).filter(
      (p) => p.id !== product.id && !products.some((p2) => p2.id === p.id)
    );
    products.push(...moreProducts.slice(0, l - products.length));
  }
  return {
    results: products,
  };
}

export async function getMoreFromThisSeller(
  product: ProductDocument,
  l = DEFAULT_LIMIT
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('seller_id', '==', product.seller_id),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const snapshot = await getDocs(q);
  const products = snapshot.docs.map((doc) => doc.data()) ?? [];
  return products.filter((p) => p.id !== product.id) ?? [];
}

export async function getSellerListingsPaginated(
  sellerId: string,
  isAdmin: boolean,
  page: number,
  category: string | null = null,
  category1: string | null = null,
  brand: string | null = null,
  condition: Condition | 'all' = 'all',
  availability: Availability | 'all' = 'all',
  auctionName: string | null = 'all',
  l: number = 12
) {
  if (!sellerId && !isAdmin)
    return Promise.resolve({
      results: [],
      pageInfo: {
        page: 1,
        totalPages: 1,
      },
    });

  const limitValue = availability === 'paymentFailed' ? null : l;

  const filterConstraints = [
    ...(category !== 'all' ? [where('category', '==', category)] : []),
    ...(category1 !== 'all' ? [where('category1', '==', category1)] : []),
    ...(condition !== 'all' ? [where('condition', '==', condition)] : []),
    ...(brand !== 'all' ? [where('brand', '==', brand)] : []),
    ...(availability !== 'all'
      ? [...getAvailabilityConstraint(availability)]
      : []),
    ...(auctionName !== 'all'
      ? [where('auction_name', '==', auctionName)]
      : []),
  ];

  const orderByField =
    availability === 'notSold' || availability === 'paymentFailed'
      ? null
      : 'date_added';

  const noLimitQ = query(
    productsRef,
    where('seller_id', '==', sellerId),
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : [])
  );
  const noLimitAdminQ = query(
    productsRef,
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : [])
  );
  const count = (
    await getCountFromServer(isAdmin ? noLimitAdminQ : noLimitQ)
  ).data().count;
  const totalPages = limitValue ? Math.ceil(count / limitValue) : 1;

  // handle pagination
  let lastVisible = null;
  if (page > 1 && page <= totalPages) {
    const q = query(
      productsRef,
      where('seller_id', '==', sellerId),
      ...filterConstraints,
      ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
      ...(limitValue ? [limit((page - 1) * limitValue)] : [])
    );

    const adminQ = query(
      productsRef,
      ...filterConstraints,
      ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
      ...(limitValue ? [limit((page - 1) * limitValue)] : [])
    );

    const snap = await getDocs(isAdmin ? adminQ : q);
    lastVisible = snap.docs[snap.docs.length - 1];
  }

  const q = query(
    productsRef,
    where('seller_id', '==', sellerId),
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
    ...(lastVisible ? [startAfter(lastVisible)] : []),
    ...(limitValue ? [limit(limitValue)] : [])
  );
  const adminQ = query(
    productsRef,
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
    ...(lastVisible ? [startAfter(lastVisible)] : []),
    ...(limitValue ? [limit(limitValue)] : [])
  );

  const snap = await getDocs(isAdmin ? adminQ : q);

  let results = snap.docs.map((d) => cleanLocation(d.data()));
  if (availability === 'paymentFailed') {
    results = results.filter((r) => r.og_price !== r.price);
  }
  return {
    results,
    pageInfo: {
      page,
      totalPages,
    },
  };
}

export async function deleteListing(productId: string) {
  const docRef = doc(productsRef, productId);
  await updateDoc(docRef, {
    is_draft: false,
    date_updated: Date.now(),
    date_added: deleteField(),
    out_of_stock: true,
  });
}

export async function markAsSold(product: ProductDocument): Promise<void> {
  const bikeInfo: {
    item_id: string;
    value: number;
    year?: number;
    make?: string;
    model?: string;
  } = { item_id: product.id, value: product.price };
  if (product.year) bikeInfo.year = product.year;
  if (product.make) bikeInfo.make = product.make;
  if (product.model) bikeInfo.model = product.model;
  logEvent('bike_sold', bikeInfo);
  const ref = doc(productsRef, product.id);
  return updateDoc(ref, {
    stock: 0,
    out_of_stock: true,
    sold_date: Date.now(),
    date_added: deleteField(),
  });
}

// we'll discuss this one
// prob use our ai guy
export async function getFreqPurchasedWith(
  categories: string[],
  l = DEFAULT_LIMIT
) {
  const q = query(
    productsRef,
    where(`category${categories.length - 1}`, '==', categories.pop()),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export function getProductId(): string {
  const newDocRef = doc(productsRef);
  const id = newDocRef.id;
  return id;
}

export async function addProductDoc(data: ProductDocument) {
  const parsedData = productSchema.parse(data);
  const docRef = doc(productsRef, parsedData.id);

  Object.keys(parsedData).forEach(
    (key) =>
      // @ts-ignore typescript doesnt' like this
      parsedData[key] === undefined && delete parsedData[key]
  );
  return setDoc(docRef, parsedData);
}

export async function updateProductListingDoc(data: ProductDocument) {
  const parsedData = productSchema.parse(data);
  const docRef = doc(productsRef, parsedData.id);
  // Not using { merge: true} because the edit listing page makes sure the product passed is fully correct
  return setDoc(docRef, parsedData);
}

export async function addProductDraftDoc(data: Partial<ProductDocument>) {
  // filter out any undefined values, todo do this better
  removeUndefinedRecursive(data);
  const docRef = doc(productsRef, data.id);
  return setDoc(docRef, data);
}

export async function addProductToDataDepot(data: ProductDocument) {
  return await httpsCallable<{ product: ProductDocument }>(
    functions,
    'dataDepot-v2_addProductToDataDepot'
  )({ product: data });
}

export function removeUndefinedRecursive(obj: any) {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') {
      removeUndefinedRecursive(obj[key]);
    } else if (obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
}

export async function updateProductDraftDoc(data: Partial<ProductDocument>) {
  const docRef = doc(productsRef, data.id);
  removeUndefinedRecursive(data);
  return updateDoc(docRef, data);
}

export async function reactivateProductDoc(product_id: string) {
  const docRef = doc(productsRef, product_id);
  return updateDoc(docRef, {
    date_updated: Date.now(),
    is_draft: false,
    out_of_stock: false,
  });
}

// Constraints
function getBikeConstraints(bikes: {
  years: string[];
  makes: string[];
  models: string[];
}) {
  const allBikeCombos = [];
  const allBikeMakes = [];
  const allBikeMakesModels = [];
  if (!bikes.makes?.length) return null;

  for (const make of bikes.makes) {
    allBikeMakes.push({ make });
    for (const model of bikes.models) {
      allBikeMakesModels.push({ make, model });
      for (const year of bikes.years) {
        allBikeCombos.push({ year, make, model });
      }
    }
  }
  if (
    !allBikeCombos.length &&
    !allBikeMakes.length &&
    !allBikeMakesModels.length
  )
    return null;
  return where('fitting_bikes.bikes', 'array-contains-any', [
    ...(allBikeMakes.length ? allBikeMakes.map((b) => ({ make: b.make })) : []),
    ...(allBikeMakesModels.length
      ? allBikeMakesModels.map((b) => ({ make: b.make, model: b.model }))
      : []),
    ...(allBikeCombos.length
      ? allBikeCombos.map((b) => ({
          make: b.make,
          model: b.model,
          year: +b.year,
        }))
      : []),
  ]);
}
function getCategoryConstraint(
  categoryQueryValue: string | null,
  showDirtBikes?: boolean
) {
  return !!categoryQueryValue
    ? where('category', '==', categoryQueryValue)
    : where('category', 'in', [
        'Riding Gear',
        'Mountain Bikes',
        'Bike Parts',
        'Collectibles',
        'Accessories',
        ...(showDirtBikes ? ['Dirt Bikes'] : []),
      ]);
}

function getSubCategoryConstraint(
  cat1: string | null,
  cat2: string | null,
  cat3: string | null
) {
  return !!cat3
    ? where('category3', '==', cat3)
    : !!cat2
    ? where('category2', '==', cat2)
    : !!cat1
    ? where('category1', '==', cat1)
    : null;
}

function getSortConstraint(sortQueryValue?: string | null) {
  return sortQueryValue === 'priceAsc'
    ? orderBy('price', 'asc')
    : sortQueryValue === 'priceDesc'
    ? orderBy('price', 'desc')
    : orderBy('date_added', 'desc');
}

function getBrandsConstraint(brandsQueryValue?: string[] | null) {
  if (brandsQueryValue?.length) {
    if (brandsQueryValue.length === 1) {
      return where('brand', '==', brandsQueryValue[0]);
    } else if (brandsQueryValue.length > 1) {
      return where('brand', 'in', brandsQueryValue);
    }
  }
  return null;
}

function getBootSizesConstraint(bootSizesQueryValue?: string[] | null) {
  let numArray: number[] = [];
  if (bootSizesQueryValue?.length)
    numArray = bootSizesQueryValue.map((c) => +c);
  return !bootSizesQueryValue?.length
    ? null
    : bootSizesQueryValue.length === 1
    ? where('size.number', '==', +numArray[0])
    : where('size.number', 'in', numArray);
}

function getShirtSizesConstraint(shirtSizeConstraint?: string[] | null) {
  if (shirtSizeConstraint?.length)
    shirtSizeConstraint = shirtSizeConstraint.map((c) => c);
  return !shirtSizeConstraint?.length
    ? null
    : shirtSizeConstraint.length === 1
    ? where('size.letter', '==', shirtSizeConstraint[0])
    : where('size.letter', 'in', shirtSizeConstraint);
}

function getPantSizesConstraint(pantSizeConstraint?: string[] | null) {
  if (pantSizeConstraint?.length)
    pantSizeConstraint = pantSizeConstraint.map((c) => c);
  return !pantSizeConstraint?.length
    ? null
    : pantSizeConstraint.length === 1
    ? where('size.number', '==', +pantSizeConstraint[0])
    : where(
        'size.number',
        'in',
        pantSizeConstraint.map((c) => +c)
      );
}

/**
 * Return the size constraint filter for Firestore objects with the size.letter
 * attribute (e.g. gloves and helmets).
 */
function getLetterSizeConstraint(letterSizeConstraint?: string[] | null) {
  if (letterSizeConstraint?.length)
    letterSizeConstraint = letterSizeConstraint.map((c) => c);
  return !letterSizeConstraint?.length
    ? null
    : letterSizeConstraint.length === 1
    ? where('size.letter', '==', letterSizeConstraint[0])
    : where('size.letter', 'in', letterSizeConstraint);
}

function getGloveSizesConstraint(gloveSizeConstraint?: string[] | null) {
  return getLetterSizeConstraint(gloveSizeConstraint);
}

function getHelmetSizesConstraint(helmetSizeConstraint?: string[] | null) {
  return getLetterSizeConstraint(helmetSizeConstraint);
}

// Helpers
function argsRequireTypesense(
  args: Pick<
    BaseGetProductsArgs,
    | 'keyword'
    | 'page'
    | 'minPrice'
    | 'maxPrice'
    | 'brands'
    | 'conditions'
    | 'sort'
    | 'shipTo'
    | 'sellerType'
    | 'bikeMakes'
    | 'bikeModels'
    | 'bikeYears'
    | 'letterSizes'
    | 'numberSizes'
  >
) {
  return (
    args.sellerType ||
    args.keyword ||
    args.page > 1 ||
    ((args.minPrice || args.maxPrice) && args.sort === 'newest') ||
    (args.brands &&
      args.brands.length > 1 &&
      args.conditions &&
      args.conditions.length > 1) ||
    args.brands?.length > 30 ||
    (args.shipTo && args.shipTo.length >= 1) ||
    (args.bikeMakes && args.bikeMakes.length >= 1) ||
    (args.bikeModels && args.bikeModels.length >= 1) ||
    (args.bikeYears && args.bikeYears.length >= 1) ||
    (args.letterSizes && args.letterSizes.length >= 1) ||
    (args.numberSizes && args.numberSizes.length >= 1)
  );
}

export async function reportItem(
  id: string,
  reason: ReasonTypes,
  isUser: boolean
): Promise<void> {
  const col = isUser ? 'reports_users' : 'reports_products';
  const colRef = collection(db, col);
  const ref = doc(colRef, id);
  return setDoc(ref, { [reason]: increment(1) }, { merge: true });
}

export async function getOnSaleByCategory(category: string, l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('category', '==', category),
    where('on_sale', '==', true),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => cleanLocation(d.data()));
}

export async function getCuratedListByCategory(
  list: string,
  category: string,
  l = DEFAULT_LIMIT
) {
  const q = query(
    productsRef,
    where('category', '==', category),
    where('curated_lists', 'array-contains', list),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => cleanLocation(d.data()));
}

export async function updateProductByKey(
  id: string,
  key: keyof ProductDocument,
  value: any
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { [key]: value });
}

export async function updateProductByKeys(
  id: string,
  keyValues: Record<string, any>
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, keyValues);
}

export async function bumpProduct(id: string): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { date_added: Date.now(), bumps: increment(-1) });
}

export async function removeProductFromFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayRemove(productId) });
}

export async function addProductToFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayUnion(productId) });
}

export async function addProductToCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);
  return updateDoc(ref, { curated_lists: arrayUnion(curated_list) });
}

export async function removeProductFromCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);
  return updateDoc(ref, { curated_lists: arrayRemove(curated_list) });
}

export async function getMasterProductInfo(cat2?: CategoryDocument): Promise<{
  min: number;
  max: number;
  cat1_name: string;
  brand_name: string;
  brand_slug: string;
}> {
  if (!cat2)
    return {
      min: 0,
      max: 0,
      cat1_name: '',
      brand_name: '',
      brand_slug: '',
    };
  const minPriceQ = query(
    productsRef,
    where('category2', '==', cat2.name),
    orderBy('price', 'asc'),
    limit(1)
  );

  const maxPriceQ = query(
    productsRef,
    where('category2', '==', cat2.name),
    orderBy('price', 'desc'),
    limit(1)
  );

  const [minPrice, maxPrice, cat1Snap, brand] = await Promise.all([
    getDocs(minPriceQ),
    getDocs(maxPriceQ),
    getCategoryByID(cat2.id),
    getBrandByName(cat2.name.split(' ')[0]),
  ]);
  return {
    min: minPrice.docs[0]?.get('price') ?? 0,
    max: maxPrice.docs[0]?.get('price') ?? 0,
    cat1_name: cat1Snap?.name ?? '',
    brand_name: brand?.name ?? '',
    brand_slug: brand?.url ?? '',
  };
}

export function setVacationMode(uid: string, on = false) {
  return httpsCallable<{ uid: string; on: boolean }, void>(
    functions,
    FirebaseCallable.vacationMode
  )({ uid, on });
}

const getBrandsFromResult = (products: ProductDocument[]) =>
  [...new Set(products.map((p) => p.brand))].sort().filter(Boolean);

const getAvailabilityConstraint = (availabilityQueryValue?: Availability) => {
  if (availabilityQueryValue === 'inStock') {
    return [where('out_of_stock', '==', false)];
  }
  if (availabilityQueryValue === 'sold') {
    return [where('out_of_stock', '==', true), where('is_draft', '==', false)];
  }
  if (availabilityQueryValue === 'draft') {
    return [where('is_draft', '==', true)];
  }
  if (availabilityQueryValue === 'onSale') {
    return [where('on_sale', '==', true), where('out_of_stock', '==', false)];
  }
  if (
    availabilityQueryValue === 'notSold' ||
    availabilityQueryValue === 'paymentFailed'
  ) {
    // p.is_auction && p.out_of_stock && p.stock
    return [
      where('is_auction', '==', true),
      where('out_of_stock', '==', true),
      where('stock', '>', 0),
    ];
  }
  return [];
};

export const cleanLocation = (p: ProductDocument) => {
  if (!p) return p;
  if (p.shipment) delete p.shipment;
  return {
    ...p,
    ...(p?.location && {
      location: {
        latitude: p.location.latitude ?? 0,
        longitude: p.location.longitude ?? 0,
      },
    }),
  };
};

export const getProductDescription = (product?: ProductDocument | null) => {
  if (!product) return '';
  if (!product.description && product.description_html) {
    return stripHtml(product.description_html).result;
  }
  return product.description;
};

export function getVariationPrice(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!selectedVariations) return product.price;
  const letterSize = selectedVariations?.find((v) => isNaN(+v.size));
  const numberSize = selectedVariations?.find(
    (v) => !isNaN(+v.size) && v.is_number
  );
  const isColor = selectedVariations?.find((v) => !!v.color);
  if (letterSize && numberSize && !isColor) {
    return letterSize.price + numberSize.price;
  }
  if (letterSize) {
    if (isColor) {
      return Math.max(letterSize.price, isColor.price);
    }
    return letterSize.price;
  }
  if (numberSize) {
    if (isColor) {
      return Math.max(numberSize.price, isColor.price);
    }
    return numberSize.price;
  }
  return product.price;
}

export function getSizeFromVariation(selectedVariations: Variation[] | null) {
  const letterSize = selectedVariations?.find(
    (v) => v.size && !v.is_number && v.size !== 'N/A'
  );
  const numberSize = selectedVariations?.find(
    (v) => v.is_number && v.size && v.size !== 'N/A'
  );
  return {
    ...(letterSize && {
      letter: letterSize.size as string,
    }),
    ...(numberSize && {
      number: +numberSize.size,
    }),
  };
}

export async function getBikeGraph(year: number, make: string, model: string) {
  const q = query<ProductDocument, DocumentData>(
    productsRef,
    where('year', '==', year),
    where('make', '==', make),
    where('model', '==', model),
    orderBy('sold_date')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const bikes = snapshot.docs.map((d) => d.data());

  let sum = 0;
  let prices: number[] = [];
  let soldDates: number[] = [];

  let min = Infinity;
  let max = -Infinity;

  bikes.sort((a, b) => (a.sold_date ?? 0) - (b.sold_date ?? 0));
  bikes.forEach((bike) => {
    sum += bike.price;
    if (bike.price < min) min = bike.price;
    if (bike.price > max) max = bike.price;
    prices.push(Math.ceil(bike.price / 500) * 500);
    soldDates.push(bike.sold_date ?? 0);
  });

  const count = snapshot.size;

  return { prices, soldDates, min, max, count, avg: sum / count };
}

export async function getProductViews(id: string) {
  const productViewsRef = collection(db, 'product_views');
  const data = doc(productViewsRef, id);
  const docSnap = await getDoc(data);
  return (docSnap.data() as { views: number }) ?? { views: 0 };
}

export function generateTypesenseFilters(args: BaseGetProductsArgs) {
  let filters = '';
  if (args.keyword || (!args.category1 && !args.showDirtBikes)) {
    // NOT category = dirt bikes on search page or non category pages
    filters += `category:!="Dirt Bikes" && `;
  }
  if (args.current_cat) {
    if (args.current_cat.product_cats?.ride_type) {
      filters += `ride_types:=[\`${args.current_cat.product_cats.ride_type}\`] && `;
    }
    if (args.current_cat.product_cats?.category) {
      filters += `category:=\`${args.current_cat.product_cats.category}\` && `;
    }
    if (args.current_cat.product_cats?.category1) {
      filters += `category1:=\`${args.current_cat.product_cats.category1}\` && `;
    }
    if (args.current_cat.product_cats?.category2) {
      filters += `(category2:=\`${args.current_cat.product_cats.category2}\` || category3:=\`${args.current_cat.product_cats.category2}\`) && `;
    }
    if (args.current_cat.product_cats?.category3) {
      filters += `(category3:=\`${args.current_cat.product_cats.category3}\` || category2:=\`${args.current_cat.product_cats.category3}\`) && `;
    }
    if (args.current_cat.product_cats?.category4) {
      filters += `category4:=\`${args.current_cat.product_cats.category4}\` && `;
    }
  } else if (args.product_cats) {
    if (args.product_cats.ride_type) {
      filters += `ride_types:=\`${args.product_cats.ride_type}\` && `;
    }
    if (args.product_cats.category) {
      filters += `category:=\`${args.product_cats.category}\` && `;
    }
    if (args.product_cats.category1) {
      filters += `category1:=\`${args.product_cats.category1}\` && `;
    }
    if (args.product_cats.category2) {
      filters += `category2:=\`${args.product_cats.category2}\` && `;
    }
    if (args.product_cats.category3) {
      filters += `category3:=\`${args.product_cats.category3}\` && `;
    }
  }
  const selectedBikes = args.bikeMakes
    .map((make) => {
      return args.bikeModels.map((model) => {
        return args.bikeYears.map((year) => {
          const parsedMake = make
            ?.toString()
            .toLowerCase()
            .replace(/[^a-z0-9/]+/g, '-')
            .replace(/^-+|-+$/g, '');
          const parsedModel = model
            ?.toString()
            .toLowerCase()
            .replace(/[^a-z0-9/]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/\//g, '*');
          return {
            year,
            make: parsedMake,
            model: parsedModel,
            type: args.rideTypes?.[0] ?? 'Dirt Bikes',
          };
        });
      });
    })
    .flat()
    .flat()
    .flat();
  if (selectedBikes.length) {
    selectedBikes.forEach((b) => {
      filters += `((fitting_bikes_strings:=[\`${b.year}|${b.make}|${b.model}\`]`;
      filters += ` || fitting_bikes_strings:=[\`${b.year}|${b.make}|null\`]`;
      filters += ` || fitting_bikes_strings:=[\`null|${b.make}|null\`])`;
      filters += ` || (fitting_bikes.universal:=true && ride_types:=${b.type})) && `;
    });
    // add bike parts to filters since they are matching a bike
    filters += ' category:=`Bike Parts` && ';
  }
  if (args.seller_id) filters += `seller_id:="${args.seller_id}" && `;
  if (args.is_featured) filters += 'is_featured:true && ';
  if (args.on_sale) filters += 'on_sale:true && ';
  if (args.curated_list)
    filters += `curated_lists:=[\`${args.curated_list.join('`,`')}\`] && `;
  if (args.shipTo && args.shipTo.length) {
    filters += `(shipping_costs.code:[${args.shipTo.join(
      ','
    )}] || is_flat_rate:false) && `;
  }
  if (args.shipFrom && args.shipFrom.length) {
    const countryCodes = [...args.shipFrom];
    if (args.shipFrom.includes('EU')) {
      countryCodes.push(...EUROPE_CODES);
    } else if (args.shipFrom.includes('SAM')) {
      countryCodes.push(...SOUTH_AMERICA_CODES);
    }
    filters += `(country_code:[${countryCodes.join(',')}])`;
  }
  if (args.minPrice && args.maxPrice)
    filters += `price:[${args.minPrice}..${args.maxPrice}] && `;
  else if (args.minPrice) filters += `price:>=${args.minPrice} && `;
  else if (args.maxPrice) filters += `price:<=${args.maxPrice} && `;
  if (args.brands?.length) {
    filters += `brand:[${args.brands.map((b) => `${b}`).join(',')}] && `;
  }
  if (args.conditions?.length) {
    const condition = args.conditions.includes('New') ? 'New' : 'Used';
    // if its new , filter by new, else filter by not new
    if (condition === 'New') {
      filters += `condition:"${condition}" && `;
    } else {
      filters += `condition:!="New" && `;
    }
  }
  if (args.numberSizes?.length) {
    filters += `(size.number:[${args.numberSizes.join(
      ','
    )}] || variations.size:[${args.numberSizes.join(',')}] ) && `;
  }
  if (args.letterSizes?.length) {
    filters += `(size.letter:[${args.letterSizes.join(
      ','
    )}] || variations.size:[${args.letterSizes.join(',')}] ) && `;
  }
  if (args.genders?.length) {
    filters += `gender:${args.genders[0]} && `;
  }
  if (args.sellerType) {
    if (args.sellerType === 'dealer') {
      filters += `seller_type:"dealer" && `;
    } else {
      filters += `seller_type:!="dealer" && `;
    }
  }
  if (args.color?.length) {
    filters += `variations.color:=[${args.color
      .map((c) => `"${c}"`)
      .join(',')}] && `;
  }
  if (args.rideTypes?.length && !filters.includes('ride_types:=')) {
    filters += `ride_types:=[${args.rideTypes.map((t) => `"${t}"`)}] && `;
  }
  if (args.skus?.length) {
    filters += `(sku:=[\`${args.skus.join(
      '`,`'
    )}\`] || variations.sku:=[\`${args.skus.join(
      '`,`'
    )}\`] || mpn:=[\`${args.skus.join('`,`')}\`]) && `;
  }
  if (args.aspectRatios?.length) {
    filters += `specs.Aspect Ratio:=[${args.aspectRatios
      .map((a) => `"${a}"`)
      .join(',')}] && `;
  }
  if (args.construction?.length) {
    filters += `specs.Construction:=[${args.construction
      .map((c) => `"${c}"`)
      .join(',')}] && `;
  }
  if (args.rimDiameters?.length) {
    filters += `specs.Rim Diameter:=[${args.rimDiameters
      .map((r) => `"${r}"`)
      .join(',')}] && `;
  }
  if (args.mountingPositions?.length) {
    filters += `specs.Mounting Position:=[${args.mountingPositions
      .map((m) => `"${m}"`)
      .join(',')}] && `;
  }
  if (args.tireDiameters?.length) {
    filters += `specs.Tire Diameter:=[${args.tireDiameters
      .map((t) => `"${t}"`)
      .join(',')}] && `;
  }
  if (args.widths?.length) {
    filters += `specs.Width:=[${args.widths
      .map((w) => `"${w}"`)
      .join(',')}] && `;
  }
  if (filters.endsWith('&& ')) filters = filters.slice(0, -4);
  return filters;
}

export async function getCauses() {
  const causesRef = collection(db, 'causes');
  const q = query(causesRef);
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data() as CauseDocument);
}

export async function getCauseById(cause: string) {
  const causesRef = collection(db, 'causes');
  const ref = doc(causesRef, cause);
  const docSnap = await getDoc(ref);
  if (!docSnap.exists()) return null;
  return docSnap.data() as CauseDocument;
}

export async function getReportedProducts() {
  const reportsRef = collection(db, 'reports_products');
  const q = query(reportsRef, limit(10));
  const { docs } = await getDocs(q);
  const res: Map<string, any> = new Map();
  docs.forEach((d) => {
    res.set(d.id, d.data());
  });
  const products = await getProductsByIds([...res.keys()]);
  const productsWithReports = products.map((p) => ({
    ...p,
    reports: res.get(p.id),
  }));
  return productsWithReports;
}

export async function getPartsForYourBikeFromRecommend(bike: Bike) {
  try {
    const res = await httpsCallable<{ bike: Bike }, ProductDocument[]>(
      functions,
      'v2_getShapedPartsForBike'
    )({ bike });
    return { results: res.data };
  } catch (e) {
    logError(e);
    return { results: [] };
  }
}

export async function getDirtBikeShipping({
  productId,
  zipCode,
  pickupZip,
  bikePrice,
}: {
  productId?: string;
  zipCode: string;
  pickupZip?: string;
  bikePrice?: number;
}) {
  const res = await httpsCallable<
    {
      productId?: string;
      zipCode: string;
      pickupZip?: string;
      bikePrice?: number;
    },
    {
      total: number; // amount to charge
      quote: number; // shipping before markup
      insurance_cost: number; // w markup
      shipping_cost: number; // w markup
    }
  >(
    functions,
    FirebaseCallable.calculateBikeShipping
  )({ productId, zipCode, pickupZip, bikePrice });
  return res.data;
}

export async function getInStockCount() {
  // const snap = await db
  //       .collection("products")
  //       .where("out_of_stock", "==", false)
  //       .orderBy("date_added", "desc")
  //       .get();

  const q = query(
    productsRef,
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc')
  );
  const count = await getCountFromServer(q);
  return count.data().count;
}

export async function setStoreOnSale({
  uid,
  salePercent,
}: {
  uid: string;
  salePercent: number;
}) {
  await httpsCallable<{ uid: string; salePercent: number }, void>(
    functions,
    FirebaseCallable.setStoreOnSale
  )({ uid, salePercent });
}

const getPickedForYou = async ({
  user,
  page,
  vector_query,
  filters,
  bike,
  limit,
}: {
  user?: UserDocument;
  page?: number;
  vector_query?: string;
  filters?: PageSideFilters;
  bike?: Bike;
  limit?: number;
}) => {
  const collection = 'products_alias';
  const searches: MultiSearchRequestSchema[] = [];

  if (Object.values(filters ?? {}).every((f) => !f)) {
    filters = undefined;
  }

  let defaultFilter = ``;
  if (filters?.rideTypes?.length) {
    defaultFilter += `ride_types:=[${filters.rideTypes.map(
      (t) => `"${t}"`
    )}] && `;
  }
  if (!filters?.sellerType?.[0] || filters.sellerType[0] !== 'dealer') {
    defaultFilter += 'seller_type:!=dealer';
  } else {
    defaultFilter += `seller_type:=dealer`;
  }

  if (filters?.conditions?.length) {
    if (filters.conditions.includes('New')) {
      defaultFilter += ` && condition:="New"`;
    } else {
      defaultFilter += ` && condition:!="New"`;
    }
  }
  if (filters?.brands?.length) {
    defaultFilter += ` && brand:[${filters.brands
      .map((b) => `${b}`)
      .join(',')}]`;
  }
  if (filters?.minPrice && filters?.maxPrice) {
    defaultFilter += ` && price:[${filters.minPrice}..${filters.maxPrice}]`;
  } else if (filters?.minPrice) {
    defaultFilter += ` && price:>=${filters.minPrice}`;
  } else if (filters?.maxPrice) {
    defaultFilter += ` && price:<=${filters.maxPrice}`;
  }

  if (user?.uid) defaultFilter += ` && seller_id:!=${user.uid}`;

  if (filters?.category?.[0]) {
    defaultFilter += ` && category:=\`${filters.category[0]}\``;
  }

  const gender = user?.gender ?? 'M';
  const sizes = user?.sizes ?? {};
  if (
    !filters?.category?.some((c) => c) ||
    filters.category?.[0] === 'Riding Gear'
  ) {
    if (!filters?.category?.[1] || filters.category[1] === 'Gear') {
      let filter_by = `${defaultFilter} && category1:=Gear && gender:=${gender} && `;
      if (sizes.jersey && sizes.pants) {
        filter_by += `(category2:=\`Gear Combos\` && ((size.letter:=${sizes.jersey} || variations.size:=${sizes.jersey}) && (size.number:=${sizes.pants} || variations.size:=${sizes.pants}))`;
      }
      if (sizes.jersey) {
        if (filter_by.endsWith('))')) filter_by += ' || ';
        filter_by += `(category2:=Jerseys && (size.letter:=${sizes.jersey} || variations.size:=${sizes.jersey}))`;
      }
      if (sizes.pants) {
        if (filter_by.endsWith('))')) filter_by += ' || ';
        filter_by += `(category2:Pants && (size.number:=${sizes.pants} || variations.size:=${sizes.pants}))`;
      }
      // commented cuz sizes.letter1 not in ts schema
      if (sizes.gloves) {
        if (filter_by.endsWith('))')) filter_by += ' || ';
        filter_by += `(category2:Gloves && (size.letter1:=${sizes.gloves} || variations.size:=${sizes.gloves}))`;
      }
      // no sizes
      if (filter_by.endsWith(' && ')) filter_by = filter_by.slice(0, -4);
      else filter_by += ')';

      searches.push({
        collection,
        q: '*',
        filter_by,
        query_by: '',
        page,
        per_page: limit,
        exclude_fields: 'embedding,fitting_bikes',
        ...(vector_query && { vector_query }),
      });
    }

    if (!filters?.category?.[1] || filters.category[1] === 'Boots') {
      let filter_by = `${defaultFilter} && category1:=Boots && gender:=${gender}`;
      if (sizes.boots)
        filter_by += ` && (size.number:=${sizes.boots} || variations.size:=${sizes.boots})`;
      if (filters?.category?.[3]) {
        filter_by += ` && category3:=${filters.category[3]}`;
      }
      searches.push({
        collection,
        q: '*',
        filter_by,
        query_by: '',
        page,
        per_page: limit,
        exclude_fields: 'embedding,fitting_bikes',
        ...(vector_query && { vector_query }),
      });
    }

    if (!filters?.category?.[1] || filters.category[1] === 'Helmets') {
      let filter_by = `${defaultFilter} && category1:=Helmets && gender:=${gender}`;
      if (sizes.helmet)
        filter_by += ` && (size.letter:=${sizes.helmet} || variations.size:=${sizes.helmet})`;
      if (filters?.category?.[3]) {
        filter_by += ` && category3:=${filters.category[3]}`;
      }
      searches.push({
        collection,
        q: '*',
        filter_by,
        query_by: '',
        page,
        per_page: limit,
        exclude_fields: 'embedding,fitting_bikes',
        ...(vector_query && { vector_query }),
      });
    }

    if (
      filters?.category?.[1] &&
      !['Gear', 'Boots', 'Helmets'].includes(filters.category[1])
    ) {
      let filter_by = `${defaultFilter} && category1:=\`${filters.category[1]}\``;
      if (filters.category[2])
        filter_by += ` && category2:=\`${filters.category[2]}\``;
      searches.push({
        collection,
        q: '*',
        filter_by,
        query_by: '',
        page,
        per_page: limit,
        exclude_fields: 'embedding,fitting_bikes',
        ...(vector_query && { vector_query }),
      });
    }
  }

  const bikes = bike ? [bike] : user?.bikes ?? [];
  if (
    !filters?.category?.some((c) => c) ||
    filters.category?.[0] === 'Bike Parts'
  ) {
    let filter_by = defaultFilter;
    if (!filter_by.includes('category:='))
      filter_by += ' && category:=`Bike Parts`';
    if (filters?.category?.[1])
      filter_by += ` && category1:=\`${filters.category[1]}\``;
    if (filters?.category?.[2])
      filter_by += ` && category2:=\`${filters.category[2]}\``;
    filter_by += ' && (';
    bikes?.forEach((b, i, arr) => {
      if (b.make) {
        filter_by += ` ((fitting_bikes.bikes.make:=\`${b.make}\`)`;
        if (b.year) filter_by += ` && (fitting_bikes.bikes.year:=${b.year})`;
        if (b.model)
          filter_by += ` && (fitting_bikes.bikes.model:=\`${b.model}\`)`;
        if (i === arr.length - 1) filter_by += `))`;
        else filter_by += `) ||`;
      }
    });
    if (filter_by.endsWith(' && (')) filter_by = filter_by.slice(0, -4);
    searches.push({
      collection,
      q: '*',
      filter_by,
      query_by: '',
      page,
      per_page: limit,
      exclude_fields: 'embedding,fitting_bikes',
      ...(vector_query && { vector_query }),
    });
  }

  if (
    !filters?.category?.some((c) => c) ||
    filters.category?.[0] === 'Accessories'
  ) {
    let filter_by = defaultFilter;
    if (!filter_by.includes('category:='))
      filter_by += ' && category:=`Accessories`';
    if (filters?.category?.[1])
      filter_by += ` && category1:=\`${filters.category[1]}\``;
    if (filters?.category?.[2])
      filter_by += ` && category2:=\`${filters.category[2]}\``;
    searches.push({
      collection,
      q: '*',
      filter_by,
      query_by: '',
      page,
      per_page: limit,
      exclude_fields: 'embedding,fitting_bikes',
      ...(vector_query && { vector_query }),
    });
  }

  if (
    !filters?.category?.some((c) => c) ||
    filters.category?.[0] === 'Collectibles'
  ) {
    let filter_by = defaultFilter;
    if (!filter_by.includes('category:='))
      filter_by += ' && category:=`Collectibles`';
    if (filters?.category?.[1])
      filter_by += ` && category1:=\`${filters.category[1]}\``;
    if (filters?.category?.[2])
      filter_by += ` && category2:=\`${filters.category[2]}\``;
    searches.push({
      collection,
      q: '*',
      filter_by,
      query_by: '',
      page,
      per_page: limit,
      exclude_fields: 'embedding,fitting_bikes',
      ...(vector_query && { vector_query }),
    });
  }

  if (!searches.length)
    return {
      products: [] as ProductDocument[],
      errors: ['No searches'],
      facet_counts: [] as FacetCounts[],
    };
  try {
    const { results } = await client.multiSearch.perform<ProductDocument[]>(
      { searches },
      {
        facet_by:
          'brand,condition,category,category1,category2,category3,seller_type,gender,size,variations.color',
        max_facet_values: 25,
      }
    );
    const products: ProductDocument[] = [];
    const errors: string[] = [];
    results.forEach((r) => {
      if (r.hits?.length)
        r.hits.forEach((h) =>
          products.push({
            ...h.document,
            from_recommend: true,
          })
        );
      const err: string = (r as any)?.error;
      if (err) errors.push(err);
    });
    products.sort(() => (Math.random() > 0.5 ? 1 : -1));
    return {
      products,
      errors,
    };
  } catch (e) {
    logError(e, `getPickedForYou - web - ${user?.uid}`);
    return {
      products: [] as ProductDocument[],
      errors: [(e as Error).message],
      facet_counts: [] as FacetCounts[],
    };
  }
};

export async function sendOffersToLikers(product_id: string, price: number) {
  try {
    const sendOffer = httpsCallable<
      { product_id: string; price: number },
      { errorCount: number; successCount: number }
    >(functions, 'v2_sendOffersToLikers');

    const res = await sendOffer({ product_id, price });
    return { results: res.data };
  } catch (e) {
    logError(e);
    throw e;
  }
}

export async function getCategoryByProductCats(
  product: Pick<
    ProductDocument,
    'category' | 'category1' | 'category2' | 'category3' | 'ride_types'
  >,
  omitSEO = false
): Promise<CategoryDocument | null> {
  const colRef = collection(db, 'categories_new');
  const col1Ref = collectionGroup(db, 'category1_new');
  const col2Ref = collectionGroup(db, 'category2_new');
  const col3Ref = collectionGroup(db, 'category3_new');
  const col4Ref = collectionGroup(db, 'category4_new');
  const wheres = [
    product.ride_types?.length && product.category !== 'Collectibles'
      ? where('product_cats.ride_type', 'in', product.ride_types)
      : where('product_cats.ride_type', '==', null),
    where('product_cats.category', '==', product.category || null),
    where('product_cats.category1', '==', product.category1 || null),
    where('product_cats.category2', '==', product.category2 || null),
    where('product_cats.category3', '==', product.category3 || null),
    // where('product_cats.category4', '==', product.category4 || null), // one day
  ];
  const catDocQ = query(colRef, ...wheres, limit(1));
  const cat1DocQ = query(col1Ref, ...wheres, limit(1));
  const cat2DocQ = query(col2Ref, ...wheres, limit(1));
  const cat3DocQ = query(col3Ref, ...wheres, limit(1));
  const cat4DocQ = query(col4Ref, ...wheres, limit(1));
  const res = await Promise.all([
    getDocs(catDocQ),
    getDocs(cat1DocQ),
    getDocs(cat2DocQ),
    getDocs(cat3DocQ),
    getDocs(cat4DocQ),
  ]);
  const doc = res
    .flat()
    .find((doc) => !doc.empty && (!omitSEO || !doc.docs[0].data().is_seo_only));
  if (!doc) {
    if (product.category3) {
      product.category3 = null;
      return getCategoryByProductCats(product);
    } else if (product.category2) {
      product.category2 = null;
      return getCategoryByProductCats(product);
    } else if (product.category1) {
      product.category1 = null;
      return getCategoryByProductCats(product);
    }
    return null;
  }
  return doc.docs[0].data() as CategoryDocument;
}

const DEFAULT_BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: 'All',
    href: '/shop/all',
  },
];
export async function getBreadcrumbs(product: ProductDocument) {
  try {
    const hit = await getCategoryByProductCats(product);
    const bcs = hit?.breadcrumbs;
    return bcs?.length
      ? bcs?.slice(product.ride_types && bcs.length > 2 ? 1 : 0).map((b) => ({
          label: b.name,
          href: b.slug,
        }))
      : DEFAULT_BREADCRUMBS;
  } catch (e) {
    logError(e);
    return DEFAULT_BREADCRUMBS;
  }
}

export async function deleteAllListings(uid: string) {
  // products
  const col = collection(db, 'products');
  const q = query(
    col,
    where('seller_id', '==', uid),
    where('out_of_stock', '==', false)
  );
  let count = await getCountFromServer(q);
  while (count.data().count) {
    const qWithLimit = query(q, limit(500));
    const { docs } = await getDocs(qWithLimit);
    const batch = writeBatch(db);
    docs.forEach((doc) =>
      batch.update(doc.ref, {
        out_of_stock: true,
        date_added: deleteField(),
        date_updated: Date.now(),
        is_draft: false,
      })
    );
    await batch.commit();
    count = await getCountFromServer(q);
  }
}

export async function transferProductToSeller(
  pid: string,
  new_seller_id: string
) {
  const productsRef = collection(db, 'products');
  const ref = doc(productsRef, pid);
  await updateDoc(ref, {
    seller_id: new_seller_id,
    date_updated: Date.now(),
  });
}

export function hasValidVariations(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!product.has_variations) return true;
  // Gear combos must have 2 variations selected
  if (product.category2 === 'Gear Combos') {
    return selectedVariations?.length === 2;
  }
  // if a product has color and sizes, must select both
  if (
    product.variations?.some((v) => v.color && v.color !== 'N/A') &&
    product.variations?.some((v) => v.size !== 'N/A')
  ) {
    const hasSelectedVariations = !!selectedVariations && selectedVariations[0];
    const hasValidColor =
      hasSelectedVariations &&
      selectedVariations[0].color &&
      selectedVariations[0].color !== 'N/A';
    const hasValidSize =
      hasSelectedVariations &&
      selectedVariations[0].size &&
      selectedVariations[0].size !== 'N/A';

    const selectedVariationHasSizeAndColor = hasValidColor && hasValidSize;

    if (selectedVariationHasSizeAndColor) return true;
    return selectedVariations?.length === 2;
  }
  // otherwise just one variation is needed
  return selectedVariations?.length === 1;
}

export function getInvalidVariationsText(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!product.has_variations || !product.variations?.length) return;

  if (product.category2 === 'Gear Combos') {
    return 'Please select both a jersey and a pant';
  }

  if (selectedVariations?.length !== 1) {
    if (product.variations?.some((v) => v.color && v.color !== 'N/A')) {
      return 'Please select a color';
    } else {
      return 'Please select a size';
    }
  }
}

export type ProductCouponBundle = {
  account_id: string;
  active: boolean;
  amount_off: number | null;
  buyer_id: string | null;
  code: string;
  created: number;
  expires_at: number | null;
  first_purchase_only: boolean;
  id: string;
  is_private: boolean;
  max_per_customer: number | null;
  max_redemptions: number | null;
  max_amount: number | null;
  min_amount: number | null;
  percent_off: number | null;
  product_id: string | null;
  seller_id: string;
  redemptions: number;
};
